import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import PageContainer from '../layout/PageContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import type { PurchaseOrder } from '../../models/PurchaseOrder';
import { GET_PURCHASEORDERS_QUERY } from '../../queries/PurchaseOrderQueries';
import dayjs from "dayjs";
import { toUSCurrency } from '../../utilities/CurrencyUtilities';

const INITIAL_PAGE_SIZE = 100;

export default function PurchaseOrderList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [getPurchaseOrders, { loading: getPurchaseOrdersLoading, error: getPurchaseOrdersError, data }] = useLazyQuery(
    GET_PURCHASEORDERS_QUERY
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: PurchaseOrder[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      getPurchaseOrders();

      if (getPurchaseOrdersLoading) {
        setIsLoading(true);
      }

      if (getPurchaseOrdersError) {
        setError(getPurchaseOrdersError);
      }

      const purchaseOrders = data?.purchaseOrders || [];

      setRowsState({
        rows: purchaseOrders,
        rowCount: purchaseOrders.length || 0,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [getPurchaseOrders, getPurchaseOrdersLoading, getPurchaseOrdersError, data?.purchaseOrders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/purchase-orders/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = useCallback(() => {
    navigate('/dashboard/purchase-orders/new');
  }, [navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'vendorName',
        headerName: 'Vendor',
        valueGetter: (_, row) => row.vendor?.name,
        rowSpanValueGetter: () => null,
        width: 200
      },
      {
        field: 'dateTimePurchased',
        headerName: 'Date Purchased',
        type: 'dateTime',
        valueFormatter: (value) => dayjs(value).format(('M/D/YYYY')),
        rowSpanValueGetter: () => null,
        width: 500
      },
      {
        field: 'totalOrderCost',
        headerName: 'Total Order Cost ($)',
        type: 'number',
        valueFormatter: (value: number) => toUSCurrency(value),
        rowSpanValueGetter: () => null,
        width: 200
      }
    ],
    [],
  );

  const pageTitle = 'Purchase Orders';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <Box sx={{ height: '64vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={rowsState.rows}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              loading={isLoading || getPurchaseOrdersLoading}
              initialState={initialState}
              showToolbar
              rowSpanning
              showCellVerticalBorder
              showColumnVerticalBorder
              pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: 'transparent',
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: 'none',
                },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: 'pointer',
                },
                '.out-of-stock': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                    'rgba(198, 40, 40, 1)' :
                    'rgba(239, 83, 80, 1)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      'rgba(198, 40, 40, 1)' :
                      'rgba(239, 83, 80, 1)',
                    filter: 'brightness(1.1)'
                  }
                },
                '.low-stock': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                    'rgba(230, 81, 0, 1)' :
                    'rgba(255, 152, 0, 1)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      'rgba(230, 81, 0, 1)' :
                      'rgba(255, 152, 0, 1)',
                    filter: 'brightness(1.1)'
                  }
                }
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}